import { Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { InputChoice } from 'components/Inputs/Dropdown/dropdownConsts';
import { SelectDropdownInput } from 'components/Inputs/Dropdown/SelectDropdown/SelectDropdownInput';
import { TextDateRangeDropdownInput } from 'components/Inputs/Dropdown/TextDateRangeDropdownInput';
import { useGetFetchAndFormatBroadcastChannels } from 'components/Search/components/SearchAdvance/components/hooks';
import NewActionButton from 'components/Search/components/SearchAdvance/components/Shared/NewActionButton';
import { useAdvancedSearchContext } from 'components/Search/components/SearchAdvance/useAdvancedSearchContext';
import { useSearch } from 'components/Search/SearchContextProvider';
import { getPopoverGridInputLabelSx } from 'components/Search/styles';
import { TFunction } from 'i18next';
import { TSessionConfigData, useCosmos } from 'providers/contexts/CosmosContext';
import { BroadcastMessageItem } from 'providers/contexts/extensions/Broadcast';
import { Fragment, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { capitalize, toTitleCase } from 'remeda';
import { bloxValues } from 'themes/constants';
import {
  checkBroadcastFullAccessPermission,
  checkBroadcastSendNotificationsPermission,
  checkSiteAdminPermission,
  PermissionInfo,
} from 'utility/permissions';
import { textEllipsisSx } from 'utility/styles';
import { useNotifierModalContext } from './NotifierModalContext';

type CheckCreateBroadcastPermsArgs = {
  t: TFunction;
  sessionConfig?: TSessionConfigData;
};

export function checkCreateBroadcastPerms({
  t,
  sessionConfig,
}: CheckCreateBroadcastPermsArgs): PermissionInfo {
  // Permission Checks
  const { granted: userIsAdmin } = checkSiteAdminPermission(sessionConfig);
  const { granted: userHasFullBroadcast } =
    checkBroadcastFullAccessPermission(sessionConfig);
  const { granted: userHasSendBroadcast, name: sendBroadcastPermName } =
    checkBroadcastSendNotificationsPermission(sessionConfig);

  // Admins always have access (or user has full broadcast access)
  if (userIsAdmin || userHasFullBroadcast) {
    return {
      info: undefined,
      disabled: false,
    };
  }
  // Must send broadcast permissions to create
  if (userHasSendBroadcast) {
    return {
      info: undefined,
      disabled: false,
    };
  }
  // No access - user must have at least one
  return {
    info: t('blox.permissions.msg.required_short', {
      name: sendBroadcastPermName,
    }),
    disabled: true,
  };
}

export const NotifierSearchPreSearchContent = memo(() => {
  const { t } = useTranslation();
  const { sessionConfig } = useCosmos();

  // Notifier Modal Context
  const { handleOpenCreateMsgModal } = useNotifierModalContext();

  // Translation strings
  const strNew = t('blox.actions.new');

  // Permission checks - create modal menu button state
  const createBtnState: PermissionInfo = useMemo(() => {
    return checkCreateBroadcastPerms({
      t: t,
      sessionConfig: sessionConfig,
    });
  }, [t, sessionConfig]);

  const handleCreateMessage = () => {
    // no permissions or disabled
    if (createBtnState.disabled) {
      return;
    }
    handleOpenCreateMsgModal();
  };

  return (
    <Fragment>
      <NewActionButton
        id="notifier-search-new-message-button"
        ariaLabel="new-message-button"
        onClick={handleCreateMessage}
        open={false}
        disabled={createBtnState.disabled}
        tooltip={createBtnState.info || ''}
        showArrow={false}
      >
        {strNew}
      </NewActionButton>
    </Fragment>
  );
});
NotifierSearchPreSearchContent.displayName = 'NotifierSearchPreSearchContent';

/*
 * ----------------------------------------------------------------------------
 * Input Options
 * ----------------------------------------------------------------------------
 * These are technically also valid, but blue UI does not show them:
 *
 * const strStaged = capitalize(t('blox.notifier.status.staged', 'Staged'));
 * const strUndelivered = capitalize(t('blox.notifier.status.undelivered', 'Undelivered'));
 * const strCancelled = capitalize(t('blox.notifier.status.cancelled', 'Cancelled'));
 * { value: 'staged', label: strStaged },
 * { value: 'undelivered', label: strUndelivered },
 * { value: 'cancelled', label: strCancelled },
 *
 */

function getSendStatusOptions(t: TFunction): InputChoice[] {
  const strDraft = capitalize(t('blox.notifier.status.draft', 'Draft'));
  const strPending = capitalize(t('blox.notifier.status.pending', 'Pending'));
  const strSent = capitalize(t('blox.notifier.status.sent', 'Sent'));
  const strFailed = capitalize(t('blox.notifier.status.failed', 'Failed'));
  const strPartiallySent = toTitleCase(
    t('blox.notifier.status.partially_sent', 'Partially Sent'),
  );

  const choices: InputChoice[] = [
    { value: 'draft', label: strDraft },
    { value: 'pending', label: strPending },
    { value: 'sent', label: strSent },
    { value: 'incomplete', label: strPartiallySent },
    { value: 'failed', label: strFailed },
  ];

  return choices;
}

/*
 * ----------------------------------------------------------------------------
 * Popover Filter Fields (advanced filters popover)
 * ----------------------------------------------------------------------------
 */

export const NotifierPopoverFields = memo(() => {
  const theme = useTheme();
  const { stagedFilters, updateStagedFilterValue } = useAdvancedSearchContext();
  const { t } = useTranslation();

  // Translation strings
  const strStatus = t('blox.general.status');
  const strSendDate = t('blox.notifier.search.send_date');
  const strChannels = t('blox.notifier.search.channels');

  // Dropdown data and fetchers
  const { fetchAndFormatBroadcastChannels } = useGetFetchAndFormatBroadcastChannels();

  // Static input options
  const sendStatusOptions = useMemo(() => getSendStatusOptions(t), [t]);

  // Values in staged filters
  const stagedSendStatusValue = stagedFilters?.send_status || [];
  const stagedChannelsValue = stagedFilters?.channels || [];
  const stagedSendDateMin = stagedFilters['send_date[min]'] || '';
  const stagedSendDateMax = stagedFilters['send_date[max]'] || '';

  // Grid label styles
  const gridLabelSx = useMemo(() => {
    return getPopoverGridInputLabelSx(theme);
  }, [theme]);

  return (
    <Fragment>
      <Typography sx={gridLabelSx}>{strStatus}</Typography>
      <SelectDropdownInput
        staticChoices={sendStatusOptions}
        inputLabel={strStatus}
        value={stagedSendStatusValue}
        updateValue={(event, value) => {
          updateStagedFilterValue('send_status', value);
        }}
        multiple={true}
        infiniteScroll={false}
        showFilter={false}
        showFooter={false}
        showChipBox={false}
        popoverMatchesFormControlWidth={true}
        renderValueDisplay="chips"
        formControlVariant="outlined"
        inputPlaceholderText="stringify-options"
      />

      <Typography sx={gridLabelSx}>{strSendDate}</Typography>
      <TextDateRangeDropdownInput
        inputLabel={strSendDate}
        value={[stagedSendDateMin, stagedSendDateMax]}
        updateValue={(event, value) => {
          updateStagedFilterValue('send_date[min]', value[0]);
          updateStagedFilterValue('send_date[max]', value[1]);
        }}
        applyChangesOnPopoverClose={true}
        popoverMatchesFormControlWidth={true}
        useCalendarIconInSelect={true}
        renderValueDisplay="chips"
        formControlVariant="outlined"
      />

      <Typography sx={gridLabelSx}>{strChannels}</Typography>
      <SelectDropdownInput
        inputLabel={strChannels}
        value={stagedChannelsValue}
        updateValue={(event, values) => {
          updateStagedFilterValue('channels', values);
        }}
        fetchDataFn={fetchAndFormatBroadcastChannels}
        popoverMatchesFormControlWidth={true}
        applyChangesOnPopoverClose={true}
        infiniteScroll={true}
        showFilter={true}
        showFooter={true}
        showChipBox={true}
        multiple={true}
        renderValueDisplay="chips"
        formControlVariant="outlined"
        inputPlaceholderText="stringify-options"
      />
    </Fragment>
  );
});

NotifierPopoverFields.displayName = 'NotifierPopoverFields';

/*
 * ----------------------------------------------------------------------------
 * Advanced Filter Fields (shown below input bar when applied)
 * ----------------------------------------------------------------------------
 */

export const NotifierFilterFields = memo(() => {
  const { t } = useTranslation();
  const { validListFilter } = useSearch();
  const { showFilters, handleQpValueUpdate } = useAdvancedSearchContext();

  // Translation strings
  const strStatus = t('blox.general.status');
  const strSendDate = t('blox.notifier.search.send_date');
  const strChannels = t('blox.notifier.search.channels');

  // Dropdown data and fetchers
  const { fetchAndFormatBroadcastChannels } = useGetFetchAndFormatBroadcastChannels();

  // Static input options
  const sendStatusOptions = useMemo(() => getSendStatusOptions(t), [t]);

  // Values in valid list filter (via query params)
  const statusFilterValue = validListFilter?.send_status || [];
  const channelFilterValue = validListFilter?.channels || [];
  const sendDateMin = validListFilter?.['send_date[min]'] || '';
  const sendDateMax = validListFilter?.['send_date[max]'] || '';

  // Conditional hide/show
  const hideStatusFilter = !Boolean(showFilters?.showSendStatus);
  const hideSendDateFilter = !Boolean(showFilters?.showSendDate);
  const hideChannelsFilter = !Boolean(showFilters?.showChannels);

  return (
    <Fragment>
      <SelectDropdownInput
        staticChoices={sendStatusOptions}
        inputLabel={strStatus}
        value={statusFilterValue}
        updateValue={(event, value) => handleQpValueUpdate('send_status', value)}
        multiple={true}
        infiniteScroll={false}
        showFilter={false}
        showFooter={false}
        showChipBox={false}
        applyChangesOnPopoverClose={true}
        hidden={hideStatusFilter}
        renderValueDisplay="label"
        formControlVariant="standard"
      />

      <TextDateRangeDropdownInput
        inputLabel={strSendDate}
        value={[sendDateMin, sendDateMax]}
        updateValue={(event, value) => {
          handleQpValueUpdate('send_date[min]', value[0]);
          handleQpValueUpdate('send_date[max]', value[1]);
        }}
        applyChangesOnPopoverClose={true}
        hidden={hideSendDateFilter}
        renderValueDisplay="label"
        formControlVariant="standard"
      />

      <SelectDropdownInput
        inputLabel={strChannels}
        value={channelFilterValue}
        updateValue={(event, values) => {
          handleQpValueUpdate('channels', values);
        }}
        fetchDataFn={fetchAndFormatBroadcastChannels}
        applyChangesOnPopoverClose={true}
        infiniteScroll={true}
        showFilter={true}
        showFooter={true}
        showChipBox={true}
        multiple={true}
        hidden={hideChannelsFilter}
        renderValueDisplay="label"
        formControlVariant="standard"
      />
    </Fragment>
  );
});

NotifierFilterFields.displayName = 'NotifierFilterFields';

/**
 * Broadcast Message Title Cell
 *
 * Note: this may become 'summary' view cell in
 * future when we add that view in. For now it was
 * requested to just show title on list view...
 *
 * Added showMessage prop to optionally show
 * message below title for future summary view.
 *
 */
type BroadcastMessageTitleCellProps = {
  messageItem?: BroadcastMessageItem;
  showMessage?: boolean;
};

export const BroadcastMessageTitleCell = memo(
  ({ messageItem, showMessage = false }: BroadcastMessageTitleCellProps) => {
    const theme = useTheme();
    const title = messageItem?.title ?? '';
    const message = messageItem?.message ?? '';
    const fullTooltip = showMessage ? `${title}\n${message}` : title;

    const titleFontWeight = showMessage
      ? bloxValues.fontWeightMedium
      : bloxValues.fontWeightRegular;

    return (
      <Stack
        title={fullTooltip}
        sx={{
          width: '100%', // for text ellipsis
          gap: '2px',
        }}
      >
        <Typography
          sx={{
            // Override DataGrid cell styles for Typography
            '.MuiDataGrid-root .MuiDataGrid-cell &': {
              fontSize: bloxValues.smallFontSizeRem,
              fontWeight: titleFontWeight,
              color: theme.palette.text.primary,
              ...textEllipsisSx,
            },
          }}
        >
          {title}
        </Typography>
        {showMessage && (
          <Typography
            sx={{
              '.MuiDataGrid-root .MuiDataGrid-cell &': {
                fontSize: bloxValues.tinyFontSizeRem,
                fontWeight: bloxValues.fontWeightRegular,
                color: theme.palette.text.secondary,
                ...textEllipsisSx,
              },
            }}
          >
            {message}
          </Typography>
        )}
      </Stack>
    );
  },
);
BroadcastMessageTitleCell.displayName = 'BroadcastMessageTitleCell';

/**
 * Broadcast Message Channels Cell
 */

type BroadcastMessageChannelsCellProps = {
  messageItem?: BroadcastMessageItem;
};

export const BroadcastMessageChannelsCell = memo(
  ({ messageItem }: BroadcastMessageChannelsCellProps) => {
    const channels = messageItem?.channels || [];
    const channelNames = channels.map((channel) => channel?.name).join(', ');
    if (channels.length === 0) {
      return null;
    }
    return (
      <Tooltip
        disableInteractive={false}
        enterDelay={500}
        enterNextDelay={500}
        title={
          <Stack>
            {channels.map((channel, index) => {
              const reactkey = `channel-tooltip-${index}-${channel?.id}`;
              return (
                <Typography
                  key={reactkey}
                  sx={{
                    fontSize: bloxValues.tinyFontSizeRem,
                  }}
                >
                  {channel?.name}
                </Typography>
              );
            })}
          </Stack>
        }
      >
        <Typography
          sx={{
            width: '100%', // for text ellipsis
            // Override DataGrid cell styles for Typography
            '.MuiDataGrid-root .MuiDataGrid-cell &': {
              fontSize: bloxValues.smallFontSizeRem,
              fontWeight: bloxValues.fontWeightRegular,
              ...textEllipsisSx,
            },
          }}
        >
          {channelNames}
        </Typography>
      </Tooltip>
    );
  },
);
BroadcastMessageChannelsCell.displayName = 'BroadcastMessageChannelsCell';
